// src/components/CitationPills.tsx
import React from "react";
import { makeStyles, tokens } from "@fluentui/react-components";
import { OpenRegular } from "@fluentui/react-icons";
import { getBrandConfig } from "../brands/brand-registry";

interface Props {
  citations: string[];
  citationTitles?: Record<string, string>; // URL -> Title mapping from efficient backend service
}

function getTitleFromUrl(url: string): string {
  const parts = url.split("/");
  const last = parts[parts.length - 2] || parts[parts.length - 1];
  return last.replace(/-/g, " ");
}

/**
 * Appends UTM tracking parameters to a URL if configured.
 * Handles both URLs with existing query params and those without.
 */
function appendUtmParams(url: string, utmParams?: string): string {
  if (!utmParams) return url;
  
  // Check if URL already has query parameters
  const separator = url.includes('?') ? '&' : '?';
  // Remove leading ? from utmParams if present
  const cleanParams = utmParams.startsWith('?') ? utmParams.slice(1) : utmParams;
  return `${url}${separator}${cleanParams}`;
}

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexWrap: "wrap",
    gap: "var(--pill-citation-gap, 4px)",
    marginTop: "var(--pill-citation-margin-top, 4px)",
    marginBottom: "var(--pill-citation-margin-bottom, 4px)",
    justifyContent: "flex-start",
    alignItems: "center", // Center align with action buttons
    flex: "0 1 auto", // Allow to shrink and stay inline
    minWidth: 0, // Allow flex item to shrink below content size
    '@media (max-width: 600px)': {
      gap: 'var(--pill-citation-gap-mobile, 2px)',
      marginTop: 'var(--pill-citation-margin-top-mobile, 2px)',
      marginBottom: 'var(--pill-citation-margin-bottom-mobile, 2px)',
    },
  },
  pill: {
    display: "inline-flex",
    alignItems: "center",
    minHeight: "var(--pill-citation-height, 44px)", // WCAG AAA Target Size
    gap: tokens.spacingHorizontalXXS, // Small gap between text and icon
    paddingTop: "var(--pill-citation-padding-vertical, 6px)",
    paddingBottom: "var(--pill-citation-padding-vertical, 6px)",
    paddingLeft: "var(--pill-citation-padding-horizontal, 12px)",
    paddingRight: "var(--pill-citation-padding-horizontal, 12px)",
    backgroundColor: "var(--brand-citation, var(--brand-purple))",
    border: "1px solid var(--brand-citation, var(--brand-purple))",
    borderRadius: "var(--radius-button, 8px)",
    fontSize: "var(--citation-text-size, 14px)",
    fontFamily: "var(--font-family-base, \"Segoe UI\", \"Helvetica Neue\", Arial, sans-serif)",
    fontWeight: "var(--citation-text-weight, 400)",
    color: "var(--citation-text-color, #FFFFFF)",
    cursor: "pointer",
    transition: "all 0.2s ease-in-out",
    width: "fit-content", // Width matches content exactly
    minWidth: "fit-content", // Minimum width based on content
    maxWidth: "none", // No max width constraint
    wordWrap: "break-word",
    overflowWrap: "break-word",
    whiteSpace: "nowrap", // Prevent text wrapping within pills
    textOverflow: "ellipsis", // Add ellipsis if text is too long
    overflow: "hidden", // Hide overflow text
    textDecoration: "none", // Remove underline from links
    ":hover": {
      backgroundColor: "var(--brand-citation-hover, var(--brand-dark))",
      border: "1px solid var(--brand-citation-hover, var(--brand-dark))",
      color: "var(--citation-text-color, #FFFFFF)",
      transform: "translateY(-1px)",
      boxShadow: tokens.shadow4,
      textDecoration: "none", // Ensure no underline on hover
    },
    ":active": {
      transform: "translateY(0px)",
      boxShadow: tokens.shadow2,
    },
    '@media (max-width: 600px)': {
      minHeight: 'var(--pill-citation-height-mobile, 36px)',
      paddingTop: 'var(--pill-citation-padding-vertical-mobile, 4px)',
      paddingBottom: 'var(--pill-citation-padding-vertical-mobile, 4px)',
      paddingLeft: 'var(--pill-citation-padding-horizontal-mobile, 8px)',
      paddingRight: 'var(--pill-citation-padding-horizontal-mobile, 8px)',
      fontSize: 'var(--citation-text-size-mobile, 12px)',
    },
  },
  icon: {
    fontSize: tokens.fontSizeBase100, // Very small icon
    opacity: 0.8,
    flexShrink: 0, // Prevent icon from shrinking
  }
});

export const CitationPills: React.FC<Props> = ({ citations, citationTitles = {} }) => {
  const styles = useStyles();
  const brandConfig = getBrandConfig();
  
  if (!citations.length) return null;

  const getDisplayTitle = (url: string): string => {
    // Use backend title if available (from efficient cache + partial fetch), otherwise fallback to URL parsing
    return citationTitles[url] || getTitleFromUrl(url);
  };

  const viewSourcePrefix = brandConfig.citations?.viewSourcePrefix || 'View source: ';
  const opensInNewTab = brandConfig.accessibility?.opensInNewTab ?? 'opens in new tab';
  // UTM tracking params for analytics (optional, configured per brand)
  const utmParams = (brandConfig.citations as { citationUtmParams?: string })?.citationUtmParams;

  return (
    <div className={styles.container}>
      {citations.map((url, index) => {
        const trackedUrl = appendUtmParams(url, utmParams);
        return (
          <a
            key={index}
            href={trackedUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.pill}
            aria-label={`${viewSourcePrefix}${getDisplayTitle(url)} (${opensInNewTab})`}
          >
            {getDisplayTitle(url)}
            <OpenRegular className={styles.icon} aria-hidden="true" />
          </a>
        );
      })}
    </div>
  );
};

